import React from "react";
import Card from "components/card";
import { useDispatch } from "react-redux";
import Crudoption from "components/dropdown/crudoption";
import dayjs from "dayjs";
import {
    CalendarDays,
    Clock3,
    FileText,
} from "lucide-react";
import {
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import {
    DATE_FORMAT_MDY,
    TIME_FORMAT_12HM,
} from "variables/constants";

import {
    approveTimecardUpdateRequest,
    rejectTimecardUpdateRequest,
} from "../../../../../redux/timecardupdaterequest/timecardUpdateRequestState";
import EmployeeProfile from "components/card/EmployeeProfile";

const formatTime = (time) =>
    time ? dayjs(time).format(TIME_FORMAT_12HM) : "-";

const TimeComparison = ({
    title,
    originalIn,
    originalOut,
    requestedIn,
    requestedOut,
    fallback = "-",
}) => {

    const originalInTime = originalIn
        ? dayjs(originalIn).format("HH:mm:ss")
        : null;

    const originalOutTime = originalOut
        ? dayjs(originalOut).format("HH:mm:ss")
        : null;

    const requestedInTime = requestedIn
        ? dayjs(requestedIn).format("HH:mm:ss")
        : null;

    const requestedOutTime = requestedOut
        ? dayjs(requestedOut).format("HH:mm:ss")
        : null;

    const originalInDisplay = originalIn
        ? dayjs(originalIn).format(TIME_FORMAT_12HM)
        : "";

    const originalOutDisplay = originalOut
        ? dayjs(originalOut).format(TIME_FORMAT_12HM)
        : "";

    const requestedInDisplay = requestedIn
        ? dayjs(requestedIn).format(TIME_FORMAT_12HM)
        : "";

    const requestedOutDisplay = requestedOut
        ? dayjs(requestedOut).format(TIME_FORMAT_12HM)
        : "";

    const inChanged = requestedInTime !== originalInTime;
    const outChanged = requestedOutTime !== originalOutTime;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <Clock3 className="w-4 h-4 text-amber-500" />
                <span className="font-semibold text-sm title-text text-sm">
                    {title}
                </span>
            </div>

            <div className="grid md:grid-cols-2 gap-2">
                <div className="rounded-xl border bg-slate-50 p-2 dark-mode-card">
                    <p className="text-[10px] uppercase font-semibold muted-text">
                        Logged
                    </p>

                    <p className="font-semibold title-text text-sm">
                        {originalIn || originalOut
                            ? `${originalInDisplay} - ${originalOutDisplay}`
                            : fallback}
                    </p>
                </div>

                <div className="rounded-xl border border-blue-200 bg-blue-50 p-2 dark:bg-blue-900/20">
                    <p className="text-[10px] uppercase font-semibold text-blue-600 dark:text-blue-100">
                        Requested
                    </p>

                    <p className="font-semibold text-sm">
                        <span
                            className={
                                inChanged
                                    ? "text-blue-600 dark:text-blue-100 font-bold"
                                    : "title-text"
                            }
                        >
                            {requestedInDisplay}
                        </span>

                        {" - "}

                        <span
                            className={
                                outChanged
                                    ? "text-blue-600 dark:text-blue-100 font-bold"
                                    : "title-text"
                            }
                        >
                            {requestedOutDisplay}
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

const TimecardUpdateRequestCard = ({
    workflow,
}) => {
    const dispatch = useDispatch();

    const recordData = workflow;
    if (!recordData) {
        return null;
    }

    return (
        <Card className="rounded-2xl border-2 border-slate-200 overflow-hidden dark-mode-card">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/20 p-4">
                <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                        <div>
                            <EmployeeProfile
                                record={recordData}
                                nameInitialBg="bg-orange-100"
                                size="w-8 h-8 sm:!w-8 sm:!h-8"
                                empNameClass="title-text font-semibold"
                            />
                        </div>
                    </div>

                    <div
                        className={`status-indicator ${recordData.status_name?.toLowerCase()}`}
                    >
                        {recordData.status_name}
                    </div>
                </div>
            </div>

            <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 text-base muted-text">
                    <CalendarDays className="w-4 h-4 text-red-400 dark:text-red-500" />
                    <h2>Date:</h2>
                    <span className="font-medium">
                        {recordData.date
                            ? dayjs(recordData.date).format(DATE_FORMAT_MDY)
                            : "-"}
                    </span>
                </div>
                <TimeComparison
                    title="Schedule"
                    originalIn={recordData.original_schedule_time_in}
                    originalOut={recordData.original_schedule_time_out}
                    requestedIn={recordData.requested_schedule_time_in}
                    requestedOut={recordData.requested_schedule_time_out}
                    fallback={recordData.schedule_type_name ?? "-"}
                />

                <TimeComparison
                    title="Punches"
                    originalIn={recordData.original_punch_in}
                    originalOut={recordData.original_punch_out}
                    requestedIn={recordData.requested_punch_in}
                    requestedOut={recordData.requested_punch_out}
                    fallback="-:-"
                />

                <div className="rounded-xl border bg-slate-50 p-3 dark-mode-card">
                    <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 muted-text" />

                        <span className="text-[11px] uppercase tracking-wide font-semibold muted-text dark:text-slate-300">
                            Reason
                        </span>
                    </div>

                    <p className="text-sm whitespace-pre-wrap title-text">
                        {recordData.reason || "-"}
                    </p>
                </div>
                {recordData.rejection_reason && (
                    <div className="rounded-xl border bg-slate-50 p-3 dark-mode-card">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 muted-text" />

                            <span className="text-[11px] uppercase tracking-wide font-semibold muted-text dark:text-slate-300">
                                Rejection Reason
                            </span>
                        </div>

                        <p className="text-sm whitespace-pre-wrap title-text">
                            {recordData.rejection_reason}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex justify-end pb-3 px-4">
                <Crudoption
                    recordId={recordData.tur_id}
                    allowView={false}
                    allowEdit={false}
                    allowDelete={false}
                    approveHandler={(turId) =>
                        dispatch(approveTimecardUpdateRequest({ tur_id: turId }))
                    }
                    rejectHandler={(turId, reason) =>
                        dispatch(
                            rejectTimecardUpdateRequest({
                                tur_id: turId,
                                tur_rejection_reason: reason,
                            })
                        )
                    }
                    confirmationReason={{
                        enabled: true,
                        label: "Rejection Reason",
                        placeholder: "Enter reason for rejecting this request...",
                        required: true,
                    }}

                    buttonClassName = "cursor-pointer rounded-full px-4 py-0.5 bg-green-600 text-white text-sm"
                    additionalMenu={[
                        {
                            key: "approve",
                            icon: <CheckOutlined />,
                            label: "Approve",
                            confirm: "approve",
                            available:
                                recordData.status_name === "Pending"
                                    ? 1
                                    : 0,
                        },
                        {
                            key: "reject",
                            icon: (
                                <CloseOutlined className="text-red-600" />
                            ),
                            label: "Reject",
                            confirm: "reject",
                            available:
                                recordData.status_name === "Pending"
                                    ? 1
                                    : 0,
                        },
                    ]}
                />
            </div>
        </Card>
    );
};


export default (TimecardUpdateRequestCard);